import { useEffect, useMemo, useRef, useState } from "react";

import { resolveApiUrl } from "@/lib/api";
import type { FrontendErrorLog } from "@/observability/frontendTypes";

type Options = {
  enabled?: boolean;
  maxEvents?: number;
};

type StreamState = {
  events: FrontendErrorLog[];
  connected: boolean;
  error: string | null;
};

const FRONTEND_STREAM_URL = "/observability/frontend/stream";
const STREAM_EVENT_NAME = "error";
const DEFAULT_MAX_EVENTS = 100;

export function useFrontendErrorStream(options: Options = {}): StreamState {
  const enabled = options.enabled ?? true;
  const maxEvents = options.maxEvents ?? DEFAULT_MAX_EVENTS;
  const [events, setEvents] = useState<FrontendErrorLog[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }
    const url = resolveApiUrl(FRONTEND_STREAM_URL);
    const source = new EventSource(url);
    sourceRef.current = source;

    const handleStreamEvent = (event: Event) => {
      if (!(event instanceof MessageEvent)) {
        setConnected(false);
        setError("Connection lost");
        return;
      }
      try {
        const parsed = JSON.parse(event.data) as FrontendErrorLog;
        setEvents((prev) => {
          const next = [...prev, parsed];
          if (next.length > maxEvents) {
            next.splice(0, next.length - maxEvents);
          }
          return next;
        });
        setConnected(true);
        setError(null);
      } catch (parseError) {
        setError("Failed to parse error payload");
      }
    };

    const handleOpen = () => {
      setConnected(true);
      setError(null);
    };

    source.addEventListener(STREAM_EVENT_NAME, handleStreamEvent);
    source.addEventListener("heartbeat", () => setConnected(true));
    source.addEventListener("open", handleOpen);

    return () => {
      source.removeEventListener(STREAM_EVENT_NAME, handleStreamEvent);
      source.close();
      sourceRef.current = null;
    };
  }, [enabled, maxEvents]);

  return useMemo(
    () => ({
      events,
      connected,
      error,
    }),
    [events, connected, error],
  );
}
